// React and hooks
import React, { useEffect, useState } from 'react';

// Assets
import logo from '../assets/Logo.png';
import background from '../assets/SGI.avif';

// Theme hook
import useTheme from '../hooks/useTheme';

// Icons
import { Eye, EyeOff, Sun, Moon, Globe } from 'lucide-react';

// API client
import api from '../lib/api';

// i18n
import { useTranslation } from 'react-i18next';

const LoginScreen = ({ onLoginSuccess }) => {
  // Version
  const APP_VERSION = "CDM Prime – Version 1.0 Beta";

  // State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();

  // Auto-login if token exists
  useEffect(() => {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
    if (auth?.token) onLoginSuccess?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle login request
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      localStorage.removeItem('auth');
      const { data } = await api.post('/auth/login', { username, password });

      const token = data?.token || data?.accessToken;
      const role = data?.role || data?.roles || 'USER';
      const success = data?.success !== false;

      if (token && success) {
        localStorage.setItem('auth', JSON.stringify({ token, username, role }));
        onLoginSuccess?.();
      } else {
        setError(data?.message || t("invalidCredentials"));
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        (status === 401
          ? t("invalidCredentials")
          : status === 403
          ? t("forbidden")
          : t("serverError"));
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Base button style
  const baseBtn =
    theme === 'dark'
      ? "w-full py-2 rounded-md font-semibold shadow transition duration-200 bg-white text-black hover:bg-black hover:text-white hover:scale-105 disabled:opacity-60"
      : "w-full py-2 rounded-md font-semibold shadow transition duration-200 bg-gray-900 text-white hover:bg-white hover:text-black hover:scale-105 disabled:opacity-60";

  // Toggle language
  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center px-4 relative"
      style={{ backgroundImage: `url(${background})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white bg-opacity-80 dark:bg-black dark:bg-opacity-70 z-0" />

      {/* Language toggle */}
      <button
        onClick={toggleLanguage}
        className="absolute top-4 left-4 z-10 text-black dark:text-white"
        title={t('changeLanguage')}
      >
        <Globe size={22} />
      </button>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 z-10 text-black dark:text-white"
        title={t("toggleTheme")}
      >
        {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
      </button>

      {/* Login card */}
      <div className="relative z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-lg p-6 w-full max-w-sm space-y-4">
        <div className="text-center">
          <img src={logo} alt="Logo" className="w-20 mx-auto mb-1" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {t("companyName")}
          </h1>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200">
            {t("enterpriseSystem")}
          </h2>
        </div>

        <h2 className="text-center text-xl font-bold text-gray-800 dark:text-gray-200">
          {t("login")}
        </h2>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="text"
            placeholder={t("username")}
            className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={loading}
            autoComplete="username"
          />

          {/* Password input */}
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t("password")}
              className="w-full px-4 py-2 border rounded-md pr-10 dark:bg-gray-700 dark:text-white dark:border-gray-600"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="current-password"
            />
            {password && (
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-black dark:text-white"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            )}
          </div>

          {/* Submit button */}
          <button type="submit" className={baseBtn} disabled={loading}>
            {loading ? t("signingIn") : t("logIn")}
          </button>
        </form>

        {/* Error */}
        {error && (
          <p className="text-center text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

      </div>

      {/* Version label */}
      <p className="absolute bottom-6 text-center w-full text-sm text-gray-700 dark:text-gray-300 z-10">
        {APP_VERSION}
      </p>
    </div>
  );
};

export default LoginScreen;
